import Icon from '@mdi/react';
import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { TFunction } from 'i18next';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { mdiFacebook, mdiInstagram, mdiTwitter, mdiYoutube } from 'src/assets/icons/mdiIcons';
import { BaseLink } from '../Link';

const socialLinksStyles = pdsTv({
  slots: {
    list: ['pds-m-0 pds-flex pds-list-none pds-gap-2 pds-p-0'],
    listItem: '',
    link: ['pds-border-none pds-bg-transparent pds-text-neutral-text', 'pds-no-underline'],
    iconWrapper: [
      'pds-rounded-circle pds-bg-neutral',
      'pds-border-thick pds-border-neutral-text/[50%]',
      'pds-inline-flex pds-items-center pds-justify-center pds-p-3',
      'hover:pds-bg-neutral-text hover:pds-text-neutral',
      'pds-transition-colors pds-duration-300',
    ],
  },
});

interface StaticSocialLink {
  title: string;
  url: string;
  icon: string;
}

const getStaticSocialLinks = (t: TFunction): StaticSocialLink[] => [
  {
    title: t('Navigate to our Facebook page'),
    url: 'https://www.facebook.com/purina',
    icon: 'facebook',
  },
  {
    title: t('Navigate to our YouTube page'),
    url: 'https://www.youtube.com/user/ThePurinaNetwork',
    icon: 'youtube',
  },
  {
    title: t('Navigate to our Instagram page'),
    url: 'https://www.instagram.com/purina',
    icon: 'instagram',
  },
];

const SocialParade: React.FC<{ title: string }> = function ({ title }) {
  const { t } = useTranslation();
  const styles = React.useMemo(() => socialLinksStyles(), []);

  const staticSocialLinks = getStaticSocialLinks(t);

  const onSocialClick = (link: StaticSocialLink) => {
    sendIt({
      event: 'follow_us',
      eventCategory: 'follow_us',
      eventAction: `clicked:${link.icon}`,
      eventLabel: '(not set)',
      eventParams: {
        social_network: link.icon,
      },
    });
  };

  const getIconComponent = (icon: string) => {
    switch (icon) {
      case 'facebook':
        return mdiFacebook;
      case 'twitter':
        return mdiTwitter;
      case 'youtube':
        return mdiYoutube;
      case 'instagram':
        return mdiInstagram;
      default:
        return '';
    }
  };

  return (
    <div className="pds-space-y-3.5">
      <h2 className="pds-sr-only pds-text-title-sm pds-font-extrabold pds-text-neutral-text md:pds-not-sr-only">
        {title}
      </h2>

      <ul className={styles.list()}>
        {staticSocialLinks.map(link => {
          const iconComponent = getIconComponent(link.icon);
          if (!iconComponent) return null;

          return (
            <li key={`${link.title}-social-parade`} className={styles.listItem()}>
              <BaseLink
                to={link.url}
                analytics={false}
                hideExternalIcon
                onClick={() => onSocialClick(link)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.title}
                className={styles.link()}
              >
                <span className={styles.iconWrapper()}>
                  <Icon path={iconComponent} size={1} />
                </span>
                <span className="pds-sr-only">{link.title}</span>
              </BaseLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SocialParade;
